import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { Box, Typography } from "@mui/material";
import { type MotionProps, motion, useReducedMotion } from "framer-motion";
import { type FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { AnnieDemoReplacementReveal } from "../../hooks/useAnnie";
import { swapNurseInHistory, updateHistoryActionsWithNegativeResponse } from "../../hooks/useDemoAgentState";
import { FONT_SIZE, INK, MOTION } from "../../theme/tokens";
import { StableDemoAgent } from "../MockChatWidget/MockChatWidget";
import MetaChip from "../UIToolkit/Badges/MetaChip";
import { PersonActionRow } from "../UIToolkit/Cells/PersonActionRow";
import { ChatThinkingIndicator } from "../UIToolkit/Chat/ChatThinkingIndicator";
import { AccentIconTile } from "../UIToolkit/molecules/AccentIconTile";
import { demoReplyArrivalMs, demoReplyCount } from "./annieDemoReplacement";

type Props = {
	reveal: AnnieDemoReplacementReveal;
	/** Keys the outreach timeline, so reopening the panel shows the same plan. */
	proposalId: string;
	dataTestId: string;
};

/** A landed reply slides up and fades in; the acceptance also settles in from a hair smaller. */
const REPLY_ENTER: MotionProps = {
	initial: { opacity: 0, transform: "translateY(8px)" },
	animate: { opacity: 1, transform: "translateY(0px)" },
	transition: { duration: MOTION.DURATION.ENTRANCE_MD / 1000, ease: MOTION.EASE_OUT_POINTS },
};
const ACCEPTED_ENTER: MotionProps = {
	initial: { opacity: 0, transform: "translateY(8px) scale(0.97)" },
	animate: { opacity: 1, transform: "translateY(0px) scale(1)" },
	transition: { type: "spring", ...MOTION.SPRING_SETTLE },
};
const CHIP_ENTER: MotionProps = {
	initial: { opacity: 0, transform: "scale(0.95)" },
	animate: { opacity: 1, transform: "scale(1)" },
	transition: { duration: MOTION.DURATION.ENTRANCE_SM / 1000, ease: MOTION.EASE_OUT_POINTS },
};

const repliesLandedBy = (reveal: AnnieDemoReplacementReveal, now: number): number =>
	Array.from({ length: demoReplyCount(reveal.replacement) }, (_, index) => index).filter((index) => reveal.startedAtMs + demoReplyArrivalMs(index) <= now)
		.length;

/**
 * A demo department's simulated replacement search: each contacted nurse's reply lands as its own row,
 * the last one accepts. Progress is derived from the reveal's start, so reopening the panel resumes.
 */
export const AnnieDemoReplacementFeed: FC<Props> = ({ reveal, proposalId, dataTestId }) => {
	const { t } = useTranslation();
	const { replacement } = reveal;
	const replyCount = demoReplyCount(replacement);
	const [shownCount, setShownCount] = useState(() => repliesLandedBy(reveal, Date.now()));
	const isComplete = shownCount === replyCount;
	const reduceMotion = useReducedMotion();
	const repliesRef = useRef<HTMLDivElement>(null);

	// Bring each landed reply (and the waiting line under it) into view: the card grows below the chat's fold.
	useEffect(() => {
		if (shownCount === 0) return;
		repliesRef.current?.scrollIntoView({ block: "end", behavior: reduceMotion ? "auto" : "smooth" });
	}, [shownCount, reduceMotion]);

	useEffect(() => {
		if (shownCount >= replyCount) return;
		const waitMs = Math.max(0, reveal.startedAtMs + demoReplyArrivalMs(shownCount) - Date.now());
		// A timer can fire a hair before the clock reads its due time; always advance at least one reply,
		// or an unchanged count would never re-arm the effect and the search would stall.
		const timeoutId = setTimeout(() => setShownCount((count) => Math.min(replyCount, Math.max(count + 1, repliesLandedBy(reveal, Date.now())))), waitMs);
		return () => clearTimeout(timeoutId);
	}, [shownCount, replyCount, reveal]);

	// The outreach timeline (messages sent, the planned call, the agency fallback) is the old demo's
	// StableDemoAgent; each landed reply is written into it the same way that demo did.
	const outreachKey = `${proposalId}-${replacement.eligible_count}`;
	useEffect(() => {
		// Every reply counts as a response, the acceptance included.
		if (shownCount > 0) updateHistoryActionsWithNegativeResponse(outreachKey, shownCount);
		if (isComplete) swapNurseInHistory(outreachKey, replacement.accepted);
	}, [outreachKey, shownCount, isComplete, replacement]);

	return (
		<Box data-testid={dataTestId} sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
			<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
				<Typography sx={{ fontSize: FONT_SIZE.LABEL_XS, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: INK.MUTED }}>
					{t("annie.proposal.demo_replacement.title")}
				</Typography>
				{isComplete && (
					<motion.div {...(reduceMotion ? {} : CHIP_ENTER)}>
						<MetaChip label={t("annie.proposal.demo_replacement.covered")} tone="success" dataTestId={`${dataTestId}-covered`} />
					</motion.div>
				)}
			</Box>
			{/* The timeline reserves an empty opposite-content column; collapse it so the steps use the card's width. */}
			<Box sx={{ "&& .MuiTimeline-root": { p: 0, m: 0 }, "&& .MuiTimelineItem-root::before": { flex: 0, p: 0 } }}>
				<StableDemoAgent
					messageId={proposalId}
					messageTimestamp={String(reveal.startedAtMs)}
					eligibleNursesCount={replacement.eligible_count}
					hideFuture={isComplete}
				/>
			</Box>
			{/* Replies land after the card renders; announce each one to screen readers. */}
			<Box ref={repliesRef} aria-live="polite" sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
				{replacement.declined.slice(0, shownCount).map((name, index) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: replies only append, so position is stable; two nurses can share a name.
					<motion.div key={`declined-${index}`} {...(reduceMotion ? {} : REPLY_ENTER)}>
						<PersonActionRow
							dense
							name={name}
							meta={t("annie.proposal.demo_replacement.declined")}
							action={<MetaChip label={t("annie.proposal.demo_replacement.declined_chip")} tone="neutral" />}
							dataTestId={`${dataTestId}-declined-${index}`}
						/>
					</motion.div>
				))}
				{isComplete ? (
					<motion.div {...(reduceMotion ? {} : ACCEPTED_ENTER)}>
						<PersonActionRow
							dense
							tone="success"
							name={replacement.accepted}
							meta={t("annie.proposal.demo_replacement.accepted")}
							avatar={
								<AccentIconTile size="xs" tone="success">
									<CheckRoundedIcon />
								</AccentIconTile>
							}
							action={<MetaChip label={t("annie.proposal.demo_replacement.accepted_chip")} tone="success" />}
							dataTestId={`${dataTestId}-accepted`}
						/>
					</motion.div>
				) : (
					<ChatThinkingIndicator text={t("annie.proposal.demo_replacement.searching")} dataTestId={`${dataTestId}-searching`} />
				)}
			</Box>
		</Box>
	);
};
